import React, { useEffect, useMemo, useState } from "react";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import Nav from "../master/NavBar";
import Header from "./HostelHeader";
import {
  GetHostelList,
  HostelBedsList,
  HostelRoomList,
  HostelStudentList,
  hostelStudentTransfer,
} from "../../api/HostelService";
import { formatToDate } from "../master/FormatToDateTime";
import { useNavigate } from "react-router-dom";

export default function HostelStudentTransfer() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [hostels, setHostels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [hostelStudents, setHostelStudents] = useState([]);

  const [filters1, setFilters1] = useState({
    hostel_id: "",
    room_id: "",
    student_id: "",
  });

  const [filters2, setFilters2] = useState({
    hostel_id: "",
    room_id: "",
    bed_id: "",
  });

  const [student, setStudent] = useState(null);

  /* =========================================================
     API HELPERS
  ========================================================= */

  const getHostelList = async () => {
    try {
      const response = await GetHostelList(`limit=all`);

      if (response.data?.status) {
        setHostels(response.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    }
  };

  const getHostelRoomsList = async () => {
    try {
      const response = await HostelRoomList(`limit=all`);

      if (response.data?.status) {
        setRooms(response.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    }
  };

  const getHostelBedsList = async () => {
    try {
      const response = await HostelBedsList(`limit=all`);

      if (response.data?.status) {
        setBeds(response.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    }
  };

  const getHostelStudents = async (roomId) => {
    if (!roomId) {
      setHostelStudents([]);
      return;
    }

    setLoading(true);

    try {
      const response = await HostelStudentList(
        `status=1&limit=all&room_id=${roomId}`,
      );

      if (response.data?.status) {
        setHostelStudents(response.data?.data || []);
      } else {
        setHostelStudents([]);
        SweetAlertShow("info", "Warning", response.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);

      try {
        await Promise.all([
          getHostelList(),
          getHostelRoomsList(),
          getHostelBedsList(),
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  /* =========================================================
     CURRENT HOSTEL CHANGE
  ========================================================= */

  const handleCurrentHostelChange = (e) => {
    const hostelId = e.target.value;

    setFilters1({
      hostel_id: hostelId,
      room_id: "",
      student_id: "",
    });

    setStudent(null);
    setHostelStudents([]);
  };

  /* =========================================================
     CURRENT ROOM CHANGE
  ========================================================= */

  const handleCurrentRoomChange = (e) => {
    const roomId = e.target.value;

    setFilters1((prev) => ({
      ...prev,
      room_id: roomId,
      student_id: "",
    }));

    setStudent(null);

    if (roomId) {
      getHostelStudents(roomId);
    } else {
      setHostelStudents([]);
    }
  };

  /* =========================================================
     CURRENT STUDENT CHANGE
  ========================================================= */

  const handleStudentChange = (e) => {
    const studentId = e.target.value;

    setFilters1((prev) => ({
      ...prev,
      student_id: studentId,
    }));

    const selectedStudent = hostelStudents.find(
      (item) => Number(item?.id) === Number(studentId),
    );

    setStudent(selectedStudent || null);
  };

  /* =========================================================
     TARGET HOSTEL CHANGE
  ========================================================= */

  const handleTargetHostelChange = (e) => {
    const hostelId = e.target.value;

    setFilters2({
      hostel_id: hostelId,
      room_id: "",
      bed_id: "",
    });
  };

  /* =========================================================
     TARGET ROOM CHANGE
  ========================================================= */

  const handleTargetRoomChange = (e) => {
    const roomId = e.target.value;

    setFilters2((prev) => ({
      ...prev,
      room_id: roomId,
      bed_id: "",
    }));
  };

  /* =========================================================
     FILTERED DATA
  ========================================================= */

  const currentRooms = useMemo(() => {
    return rooms.filter(
      (room) => String(room?.hostel_id) === String(filters1?.hostel_id),
    );
  }, [rooms, filters1?.hostel_id]);

  const targetRooms = useMemo(() => {
    return rooms.filter(
      (room) =>
        String(room?.hostel_id) === String(filters2?.hostel_id) &&
        room?.status === "Vacant",
    );
  }, [rooms, filters2?.hostel_id]);

  const targetBeds = useMemo(() => {
    return beds.filter(
      (bed) =>
        String(bed?.hostel_id) === String(filters2?.hostel_id) &&
        String(bed?.room_id) === String(filters2?.room_id) &&
        bed?.status === "Vacant",
    );
  }, [beds, filters2?.hostel_id, filters2?.room_id]);

  /* =========================================================
     FORM SUBMIT
  ========================================================= */

  const handleForm = async (e) => {
    e.preventDefault();

    if (!student?.id) {
      SweetAlertShow(
        "info",
        "Select Student",
        "Please select a student first.",
      );
      return;
    }

    if (!filters2?.hostel_id) {
      SweetAlertShow("info", "Select Hostel", "Please select transfer hostel.");
      return;
    }

    if (!filters2?.room_id) {
      SweetAlertShow("info", "Select Room", "Please select transfer room.");
      return;
    }

    if (!filters2?.bed_id) {
      SweetAlertShow("info", "Select Bed", "Please select transfer bed.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData(e.target);

      const response = await hostelStudentTransfer(formData);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Successfully Transferred",
          response?.data?.message,
        );

        navigate("/hostel/students");
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     HELPERS
  ========================================================= */

  const getHostelName = (id) => {
    return (
      hostels.find((hostel) => Number(hostel?.id) === Number(id))?.name || "-"
    );
  };

  const getRoomNumber = (id) => {
    return (
      rooms.find((room) => Number(room?.id) === Number(id))?.room_no || "-"
    );
  };

  const selectedTargetBed = targetBeds.find(
    (bed) => Number(bed?.id) === Number(filters2?.bed_id),
  );

  /* =========================================================
     UI
  ========================================================= */

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                {/* =====================================================
                    PAGE HEADER
                ===================================================== */}

                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <div
                        className="rounded-3 d-flex align-items-center justify-content-center"
                        style={{
                          width: "42px",
                          height: "42px",
                          background: "rgba(105, 108, 255, 0.12)",
                          color: "var(--bs-primary)",
                        }}
                      >
                        <i className="ri-arrow-left-right-line fs-4"></i>
                      </div>

                      <h4 className="mb-0 fw-bold">Student Transfer</h4>
                    </div>

                    <p className="text-muted mb-0 ms-1">
                      Transfer a student from one hostel room to another.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center gap-2"
                    onClick={() => navigate("/hostel/students")}
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back to Students
                  </button>
                </div>

                {/* =====================================================
                    MAIN CARD
                ===================================================== */}

                <div className="card border-0 shadow-sm overflow-hidden">
                  {/* Card Header */}

                  <div
                    className="card-header border-0 py-4 px-4"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(105,108,255,.10), rgba(105,108,255,.03))",
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: "44px",
                          height: "44px",
                          background: "var(--bs-primary)",
                          color: "#fff",
                        }}
                      >
                        <i className="ri-exchange-2-line fs-4"></i>
                      </div>

                      <div>
                        <h5 className="mb-1 fw-bold">
                          Hostel Allocation Transfer
                        </h5>

                        <small className="text-muted">
                          Select the student's current allocation and choose the
                          new room and bed.
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="card-body p-4">
                    {/* =================================================
                        STEP INDICATORS
                    ================================================= */}

                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <div
                          className="d-flex align-items-center gap-3 rounded-3 p-3"
                          style={{
                            background: "rgba(105,108,255,.07)",
                            border: "1px solid rgba(105,108,255,.15)",
                          }}
                        >
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                            style={{
                              width: "34px",
                              height: "34px",
                              background: "var(--bs-primary)",
                              color: "#fff",
                              flexShrink: 0,
                            }}
                          >
                            1
                          </div>

                          <div>
                            <div className="fw-semibold">
                              Current Allocation
                            </div>

                            <small className="text-muted">
                              Select hostel, room and student
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div
                          className="d-flex align-items-center gap-3 rounded-3 p-3"
                          style={{
                            background: "rgba(40,199,111,.07)",
                            border: "1px solid rgba(40,199,111,.15)",
                          }}
                        >
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                            style={{
                              width: "34px",
                              height: "34px",
                              background: "#28c76f",
                              color: "#fff",
                              flexShrink: 0,
                            }}
                          >
                            2
                          </div>

                          <div>
                            <div className="fw-semibold">New Allocation</div>

                            <small className="text-muted">
                              Select vacant hostel, room and bed
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =================================================
                        TRANSFER PANELS
                    ================================================= */}

                    <div className="row g-4">
                      {/* =================================================
                          CURRENT ALLOCATION
                      ================================================= */}

                      <div className="col-xl-6">
                        <div
                          className="h-100 rounded-4 p-4"
                          style={{
                            border: "1px solid rgba(108,117,125,.18)",
                            background: "rgba(108,117,125,.025)",
                          }}
                        >
                          <div className="d-flex align-items-center gap-3 mb-4">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: "42px",
                                height: "42px",
                                background: "rgba(108,117,125,.12)",
                              }}
                            >
                              <i className="ri-home-8-line fs-4"></i>
                            </div>

                            <div>
                              <h5 className="mb-0 fw-bold">
                                Current Allocation
                              </h5>

                              <small className="text-muted">
                                Where the student currently stays
                              </small>
                            </div>
                          </div>

                          <div className="row g-3">
                            {/* Current Hostel */}

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <select
                                  name="current_hostel_id"
                                  id="current_hostel_id"
                                  className="form-select"
                                  value={filters1.hostel_id}
                                  onChange={handleCurrentHostelChange}
                                >
                                  <option value="" disabled>
                                    Select Hostel
                                  </option>

                                  {hostels.map((hostel) => (
                                    <option key={hostel?.id} value={hostel?.id}>
                                      {hostel?.name}
                                    </option>
                                  ))}
                                </select>

                                <label htmlFor="current_hostel_id">
                                  Current Hostel
                                </label>
                              </div>
                            </div>

                            {/* Current Room */}

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <select
                                  name="current_room_id"
                                  id="current_room_id"
                                  className="form-select"
                                  value={filters1.room_id}
                                  onChange={handleCurrentRoomChange}
                                  disabled={!filters1.hostel_id}
                                >
                                  <option value="" disabled>
                                    Select Room
                                  </option>

                                  {currentRooms.map((room) => (
                                    <option key={room?.id} value={room?.id}>
                                      Room {room?.room_no}
                                    </option>
                                  ))}
                                </select>

                                <label htmlFor="current_room_id">
                                  Current Room
                                </label>
                              </div>
                            </div>

                            {/* Student */}

                            <div className="col-12">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  id="transfer_student_id"
                                  value={filters1.student_id}
                                  onChange={handleStudentChange}
                                  disabled={!filters1.room_id}
                                >
                                  <option value="" disabled>
                                    Select Student
                                  </option>

                                  {hostelStudents.map((item) => (
                                    <option key={item?.id} value={item?.id}>
                                      {item?.student_name} (
                                      {item?.registration_no})
                                    </option>
                                  ))}
                                </select>

                                <label htmlFor="transfer_student_id">
                                  Student
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* =================================================
                              STUDENT INFORMATION
                          ================================================= */}

                          {student?.id ? (
                            <div className="mt-4">
                              <div
                                className="rounded-4 overflow-hidden"
                                style={{
                                  border: "1px solid rgba(105,108,255,.16)",
                                  background: "rgba(105,108,255,.035)",
                                }}
                              >
                                <div className="p-3 border-bottom">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-2">
                                      <i className="ri-user-3-line text-primary fs-5"></i>

                                      <span className="fw-semibold">
                                        Student Details
                                      </span>
                                    </div>

                                    <span className="badge bg-primary-subtle text-primary">
                                      Selected
                                    </span>
                                  </div>
                                </div>

                                <div className="p-3">
                                  <div className="d-flex align-items-center gap-3 mb-3">
                                    <div
                                      className="rounded-circle d-flex align-items-center justify-content-center"
                                      style={{
                                        width: "48px",
                                        height: "48px",
                                        background: "rgba(105,108,255,.13)",
                                        color: "var(--bs-primary)",
                                      }}
                                    >
                                      <i className="ri-user-line fs-4"></i>
                                    </div>

                                    <div>
                                      <h6 className="mb-1 fw-bold">
                                        {student?.student_name || "-"}
                                      </h6>

                                      <small className="text-muted">
                                        Reg. No:{" "}
                                        {student?.registration_no || "-"}
                                      </small>
                                    </div>
                                  </div>

                                  <div className="row g-3">
                                    <InfoItem
                                      icon="ri-user-heart-line"
                                      label="Father Name"
                                      value={student?.father_name}
                                    />

                                    <InfoItem
                                      icon="ri-phone-line"
                                      label="Mobile"
                                      value={student?.mobile}
                                    />

                                    <InfoItem
                                      icon="ri-calendar-line"
                                      label="Joining Date"
                                      value={
                                        student?.joining_date
                                          ? formatToDate(student?.joining_date)
                                          : "-"
                                      }
                                    />

                                    <InfoItem
                                      icon="ri-money-rupee-circle-line"
                                      label="Final Fee"
                                      value={`₹${student?.final_fee ?? 0}`}
                                    />

                                    <div className="col-12">
                                      <div
                                        className="rounded-3 p-3"
                                        style={{
                                          background: "rgba(108,117,125,.06)",
                                        }}
                                      >
                                        <div className="d-flex gap-2">
                                          <i className="ri-map-pin-line text-muted"></i>

                                          <div>
                                            <small className="text-muted d-block">
                                              Address
                                            </small>

                                            <span className="fw-medium">
                                              {student?.address || "-"}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div
                              className="text-center rounded-4 p-4 mt-4"
                              style={{
                                border: "1px dashed rgba(108,117,125,.3)",
                              }}
                            >
                              <i className="ri-user-search-line fs-1 text-muted"></i>

                              <h6 className="mt-2 mb-1">No Student Selected</h6>

                              <small className="text-muted">
                                Select a hostel, room and student to continue.
                              </small>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* =================================================
                          TRANSFER ARROW
                      ================================================= */}

                      <div
                        className="col-xl-auto d-none d-xl-flex align-items-center justify-content-center"
                        style={{ width: "30px" }}
                      >
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: "42px",
                            height: "42px",
                            background: "var(--bs-primary)",
                            color: "#fff",
                            boxShadow: "0 5px 15px rgba(105,108,255,.25)",
                          }}
                        >
                          <i className="ri-arrow-right-line fs-5"></i>
                        </div>
                      </div>

                      {/* =================================================
                          TARGET ALLOCATION
                      ================================================= */}

                      <div className="col-xl">
                        <form onSubmit={handleForm}>
                          <input
                            type="hidden"
                            name="student_id"
                            value={student?.id || ""}
                          />

                          <div
                            className="h-100 rounded-4 p-4"
                            style={{
                              border: "1px solid rgba(40,199,111,.20)",
                              background: "rgba(40,199,111,.025)",
                            }}
                          >
                            <div className="d-flex align-items-center gap-3 mb-4">
                              <div
                                className="rounded-3 d-flex align-items-center justify-content-center"
                                style={{
                                  width: "42px",
                                  height: "42px",
                                  background: "rgba(40,199,111,.12)",
                                  color: "#28c76f",
                                }}
                              >
                                <i className="ri-home-smile-2-line fs-4"></i>
                              </div>

                              <div>
                                <h5 className="mb-0 fw-bold">Transfer To</h5>

                                <small className="text-muted">
                                  Select a vacant destination
                                </small>
                              </div>
                            </div>

                            <div className="row g-3">
                              {/* Target Hostel */}

                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    name="hostel_id"
                                    id="target_hostel_id"
                                    className="form-select"
                                    value={filters2.hostel_id}
                                    onChange={handleTargetHostelChange}
                                    disabled={!student?.id}
                                  >
                                    <option value="" disabled>
                                      Select Hostel
                                    </option>

                                    {hostels.map((hostel) => (
                                      <option
                                        key={hostel?.id}
                                        value={hostel?.id}
                                      >
                                        {hostel?.name}
                                      </option>
                                    ))}
                                  </select>

                                  <label htmlFor="target_hostel_id">
                                    New Hostel
                                  </label>
                                </div>
                              </div>

                              {/* Target Room */}

                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    name="room_id"
                                    id="target_room_id"
                                    className="form-select"
                                    value={filters2.room_id}
                                    onChange={handleTargetRoomChange}
                                    disabled={!filters2.hostel_id}
                                  >
                                    <option value="" disabled>
                                      Select Room
                                    </option>

                                    {targetRooms.map((room) => (
                                      <option key={room?.id} value={room?.id}>
                                        Room {room?.room_no}
                                      </option>
                                    ))}
                                  </select>

                                  <label htmlFor="target_room_id">
                                    New Room
                                  </label>
                                </div>
                              </div>

                              {/* Target Bed */}

                              <div className="col-12">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    name="bed_id"
                                    id="target_bed_id"
                                    className="form-select"
                                    value={filters2.bed_id}
                                    onChange={(e) =>
                                      setFilters2((prev) => ({
                                        ...prev,
                                        bed_id: e.target.value,
                                      }))
                                    }
                                    disabled={!filters2.room_id}
                                  >
                                    <option value="" disabled>
                                      Select Bed
                                    </option>

                                    {targetBeds.map((bed) => (
                                      <option key={bed?.id} value={bed?.id}>
                                        Bed {bed?.bed_no}
                                      </option>
                                    ))}
                                  </select>

                                  <label htmlFor="target_bed_id">New Bed</label>
                                </div>
                              </div>
                            </div>

                            {/* =================================================
                                DESTINATION SUMMARY
                            ================================================= */}

                            {filters2.hostel_id &&
                              filters2.room_id &&
                              filters2.bed_id && (
                                <div
                                  className="rounded-4 p-3 mt-4"
                                  style={{
                                    background: "rgba(40,199,111,.08)",
                                    border: "1px solid rgba(40,199,111,.18)",
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2 mb-3">
                                    <i className="ri-checkbox-circle-fill text-success fs-5"></i>

                                    <span className="fw-semibold">
                                      New Allocation
                                    </span>
                                  </div>

                                  <div className="row g-3">
                                    <div className="col-4">
                                      <small className="text-muted d-block">
                                        Hostel
                                      </small>

                                      <span className="fw-semibold">
                                        {getHostelName(filters2.hostel_id)}
                                      </span>
                                    </div>

                                    <div className="col-4">
                                      <small className="text-muted d-block">
                                        Room
                                      </small>

                                      <span className="fw-semibold">
                                        {getRoomNumber(filters2.room_id)}
                                      </span>
                                    </div>

                                    <div className="col-4">
                                      <small className="text-muted d-block">
                                        Bed
                                      </small>

                                      <span className="fw-semibold">
                                        {selectedTargetBed?.bed_no || "-"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}

                            {/* =================================================
                                ACTION
                            ================================================= */}

                            <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                              <button
                                type="submit"
                                className="btn btn-primary px-4 py-2 d-flex align-items-center gap-2"
                                disabled={
                                  !student?.id ||
                                  !filters2.hostel_id ||
                                  !filters2.room_id ||
                                  !filters2.bed_id ||
                                  loading
                                }
                              >
                                <i className="ri-arrow-left-right-line"></i>
                                Transfer Student
                              </button>
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>

                    {/* =====================================================
                        MOBILE TRANSFER INDICATOR
                    ===================================================== */}

                    <div className="d-flex d-xl-none justify-content-center my-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: "40px",
                          height: "40px",
                          background: "var(--bs-primary)",
                          color: "#fff",
                        }}
                      >
                        <i className="ri-arrow-down-line"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* =============================================================
   SMALL REUSABLE INFO COMPONENT
============================================================= */

function InfoItem({ icon, label, value }) {
  return (
    <div className="col-sm-6">
      <div className="d-flex gap-2">
        <div
          className="d-flex align-items-center justify-content-center rounded-2"
          style={{
            width: "34px",
            height: "34px",
            background: "rgba(108,117,125,.08)",
            flexShrink: 0,
          }}
        >
          <i className={`${icon} text-muted`}></i>
        </div>

        <div className="min-w-0">
          <small className="text-muted d-block">{label}</small>

          <span className="fw-medium text-break" style={{ fontSize: "13px" }}>
            {value || "-"}
          </span>
        </div>
      </div>
    </div>
  );
}
